import React, { useEffect, useMemo, useState } from "react";
import { BsArrowLeft } from "react-icons/bs";
import { RiErrorWarningFill } from "react-icons/ri";
import Select from "react-select";
import ClipLoader from "react-spinners/ClipLoader";
import toast from "react-hot-toast";
import { NAIRA_WALLET_STATE_OPTIONS } from "../../constants/nairaWalletStates";
import {
  buildNairaWalletFieldDefinitions,
  NairaWalletFieldDefinition,
} from "../../utils/nairaWalletFields";
import useCreateNairaWallet, {
  submitNairaWalletFromFields,
} from "../../hooks/useCreateNairaWallet";
import { getReactSelectStyles } from "../../utils/reactSelectStyles";
import {
  MAX_E164_DIGITS,
  formatPhoneNumberPreview,
  parseToE164Phone,
  sanitizePhoneInput,
} from "../../utils/phoneE164";
import { useUser } from "../../context/user-context";

type CompleteNairaWalletProfileModalProps = {
  requiredFields: unknown[];
  onClose: () => void;
  onSuccess?: () => void;
};

const inputClassName =
  "mt-2 h-[48px] w-full rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#1C2227] text-gray-900 dark:text-gray-100 px-4 text-[14px] outline-none";

const CompleteNairaWalletProfileModal: React.FC<
  CompleteNairaWalletProfileModalProps
> = ({ requiredFields, onClose, onSuccess }) => {
  const { theme } = useUser();
  const createNairaWallet = useCreateNairaWallet({
    onSuccess: () => {
      onSuccess?.();
      onClose();
    },
  });
  const fieldDefinitions = useMemo(
    () => buildNairaWalletFieldDefinitions(requiredFields),
    [requiredFields]
  );
  const selectStyles = useMemo(() => getReactSelectStyles(theme), [theme]);

  const [formValues, setFormValues] = useState<Record<string, string>>({});

  const today = new Date();
  const maxDateOfBirth = `${today.getFullYear() - 18}-${String(
    today.getMonth() + 1
  ).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const minDateOfBirth = "1900-01-01";

  useEffect(() => {
    setFormValues((prev) => {
      const next: Record<string, string> = {};
      fieldDefinitions.forEach((field) => {
        next[field.apiKey] = prev[field.apiKey] ?? "";
      });
      return next;
    });
  }, [fieldDefinitions]);

  const setFieldValue = (apiKey: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [apiKey]: value }));
  };

  const isFormValid = fieldDefinitions.every((field) => {
    const value = formValues[field.apiKey]?.trim();
    if (!value) return false;
    if (field.type === "phone") {
      return parseToE164Phone(value) !== null;
    }
    return true;
  });

  const handleSubmit = () => {
    if (!isFormValid) {
      const needsPhone = fieldDefinitions.some((f) => f.type === "phone");
      toast.error(
        needsPhone
          ? "Enter a valid phone number with country code (e.g. +2348012345678) or Nigerian 08012345678"
          : "Please complete all required fields"
      );
      return;
    }

    submitNairaWalletFromFields(
      createNairaWallet,
      fieldDefinitions,
      formValues
    );
  };

  const renderField = (field: NairaWalletFieldDefinition) => {
    const value = formValues[field.apiKey] ?? "";

    if (field.type === "date") {
      return (
        <div key={field.apiKey} className="mt-5">
          <label className="text-[14px] font-semibold text-gray-900 dark:text-gray-100">
            {field.label}
          </label>
          <input
            type="date"
            value={value}
            onChange={(e) => setFieldValue(field.apiKey, e.target.value)}
            min={minDateOfBirth}
            max={maxDateOfBirth}
            placeholder={field.placeholder}
            className={inputClassName}
          />
        </div>
      );
    }

    if (field.type === "select") {
      return (
        <div key={field.apiKey} className="mt-5">
          <label className="text-[14px] font-semibold text-gray-900 dark:text-gray-100">
            {field.label}
          </label>
          <select
            value={value}
            onChange={(e) => setFieldValue(field.apiKey, e.target.value)}
            className={inputClassName}
          >
            <option value="" disabled>
              -Select-
            </option>
            {field.options?.map((option) => (
              <option key={option} value={option}>
                {option.charAt(0).toUpperCase() + option.slice(1)}
              </option>
            ))}
          </select>
        </div>
      );
    }

    if (field.type === "phone") {
      const phonePreview = formatPhoneNumberPreview(value);

      return (
        <div key={field.apiKey} className="mt-5">
          <label className="text-[14px] font-semibold text-gray-900 dark:text-gray-100">
            {field.label}
          </label>
          <input
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={value}
            maxLength={MAX_E164_DIGITS + 1}
            onChange={(e) =>
              setFieldValue(field.apiKey, sanitizePhoneInput(e.target.value))
            }
            placeholder={field.placeholder}
            className={inputClassName}
          />
          <p className="mt-1.5 text-[12px] text-gray-500 dark:text-gray-400">
            Use country code (e.g. +234…) or Nigerian format (080…)
          </p>
          {value && !phonePreview && (
            <p className="mt-1 text-[12px] font-medium text-red-500">
              Enter a valid phone number
            </p>
          )}
          {phonePreview && (
            <p className="mt-1 text-[12px] font-medium text-[#0B75C2] dark:text-[#5CC3FF]">
              {phonePreview}
            </p>
          )}
        </div>
      );
    }

    if (field.type === "searchableSelect") {
      const selectedOption =
        NAIRA_WALLET_STATE_OPTIONS.find((option) => option.value === value) ??
        null;

      return (
        <div key={field.apiKey} className="mt-5">
          <label className="text-[14px] font-semibold text-gray-900 dark:text-gray-100">
            {field.label}
          </label>
          <div className="mt-2">
            <Select
              options={NAIRA_WALLET_STATE_OPTIONS}
              value={selectedOption}
              onChange={(selected) =>
                setFieldValue(field.apiKey, selected?.value ?? "")
              }
              placeholder="-Select-"
              isSearchable
              classNamePrefix="react-select"
              styles={selectStyles}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="fixed inset-0 z-[130] flex justify-start overflow-auto bg-[#E7EBEE] pb-16 pt-16 font-manrope dark:bg-primary_dark dark:bg-opacity-95 backdrop-blur-sm">
      <div className="mx-auto w-11/12 max-h-[72vh] overflow-hidden rounded-xl border bg-white p-6 dark:border-[#303030] dark:bg-[#192025] mds:w-8/12 lgss:flex lgss:w-2/5 lgss:flex-col xxl:w-1/3">
        <div className="flex shrink-0 items-center gap-6">
          <button type="button" onClick={onClose} aria-label="Go back">
            <BsArrowLeft className="text-[20px] text-black dark:text-white" />
          </button>
          <h4 className="font-sora text-[20px] font-medium text-gray-900 dark:text-gray-100">
            Create Naira Wallet
          </h4>
        </div>

        <div className="mt-4 min-h-0 flex-1 overflow-y-auto pb-4">
          {fieldDefinitions.length === 0 ? (
            <p className="text-[14px] text-gray-500 dark:text-gray-400">
              No profile fields are required right now.
            </p>
          ) : (
            fieldDefinitions.map((field) => renderField(field))
          )}

          <div className="mt-6 flex items-start gap-3 rounded-lg border border-[#0A7BDE]/25 bg-[#E8F3FC] p-4 dark:border-[#0A7BDE]/40 dark:bg-[#1C2227]">
            <RiErrorWarningFill className="shrink-0 text-[26px] text-secondary_button" />
            <div className="text-left">
              <h5 className="text-[14px] font-bold text-gray-900 dark:text-gray-100">
                What happens next?
              </h5>
              <p className="mt-1 text-[12px] leading-relaxed text-gray-600 dark:text-gray-400">
                Your Naira Wallet will be created once your details are
                submitted and verified.
              </p>
            </div>
          </div>
        </div>

        <button
          type="button"
          disabled={
            !isFormValid ||
            createNairaWallet.isPending ||
            fieldDefinitions.length === 0
          }
          onClick={handleSubmit}
          className={`mt-4 h-[48px] w-full shrink-0 rounded-xl text-[15px] font-semibold text-white ${
            !isFormValid ||
            createNairaWallet.isPending ||
            fieldDefinitions.length === 0
              ? "bg-gray-400"
              : "bg-secondary_button hover:brightness-110"
          }`}
        >
          {createNairaWallet.isPending ? (
            <ClipLoader color="#FFFFFF" size={24} />
          ) : (
            "Submit"
          )}
        </button>
      </div>
    </div>
  );
};

export default CompleteNairaWalletProfileModal;
